import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import { ReactElement } from 'react';
import {
  GQL_FRAGMENT_PROJECT,
  GQL_MUTATION_UPDATE_SOURCE,
  GQL_MUTATION_UPDATE_SOURCE_DOCUMENTS,
  GQL_QUERY_INDEX_SOURCE_DOCUMENT_ID,
  GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
  GQL_QUERY_INDEX_SOURCES,
  GQL_QUERY_READ_SOURCE,
} from '../utility/queries';
import { useAlerts } from '../providers/alerts';
import { notify } from '../utility/notify';
import { SourceType, ProjectState, UserCompanyLimitation } from '@sc/graphql';
import { useForm } from '@tanstack/react-form';
import { Input } from '@sc/shared-ui';
import { PlusIcon } from '@heroicons/react/24/outline';
import { SourceDocumentCard } from '../components/SourceDocumentCard';
import PictureIcon from '../assets/icons/picture.svg';
import { useUser } from '../providers/users';
import { track } from '../utility/analytics';

interface Props {
  projectUuid: string;
}

export const URLs = ({ projectUuid }: Props): ReactElement => {
  const { data: source } = useSuspenseQuery(GQL_QUERY_READ_SOURCE, {
    variables: {
      input: {
        type: SourceType.url,
        projectUuid,
      },
    },
  });

  const { data: sourceDocuments } = useSuspenseQuery(
    GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
    {
      variables: {
        dimensionsInput: {
          height: 180,
          width: 180,
        },
        input: {
          filter: {
            sourceUuid: source.readSource.uuid,
          },
          pagination: {
            perPage: 20,
            page: 1,
          },
        },
      },
    }
  );

  const { useApolloError } = useAlerts();

  const onError = useApolloError();

  const [updateSource] = useMutation(GQL_MUTATION_UPDATE_SOURCE, {
    onError,
    onCompleted: () => notify.success('Saved source name.'),
  });

  const form = useForm({
    defaultValues: {
      name: source.readSource.name ?? '',
    },
    onSubmit: ({ value }) => {
      if (value.name && value.name !== source.readSource.name) {
        void updateSource({
          variables: {
            input: {
              uuid: source.readSource.uuid,
              name: value.name,
            },
          },
        });
      }
    },
  });

  const [save, { loading }] = useMutation(
    GQL_MUTATION_UPDATE_SOURCE_DOCUMENTS,
    {
      onError,
      refetchQueries: [
        GQL_QUERY_INDEX_SOURCES,
        GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
        GQL_QUERY_INDEX_SOURCE_DOCUMENT_ID,
      ],
      update: (cache) =>
        cache.updateFragment(
          {
            id: `Project:${projectUuid}`,
            fragment: GQL_FRAGMENT_PROJECT,
          },
          (d) =>
            d
              ? {
                  ...d,
                  state: ProjectState.draft,
                  processedPc: 0,
                  sourceCount: d.sourceCount + 1,
                }
              : d
        ),
      onCompleted: () => {
        track('Source Documents Updated', {
          sourceType: SourceType.url,
          projectUuid,
          documentsCreated: 1,
          documentsDeleted: 0,
        });

        notify.success('Saved source.');
      },
    }
  );

  const urlForm = useForm({
    defaultValues: {
      url: '',
    },
    onSubmit: ({ value }) => {
      if (value.url) {
        void save({
          variables: {
            input: {
              uuid: source.readSource.uuid,
              documentsToCreate: [
                {
                  url: value.url,
                  relevance: new Date().toISOString(),
                  title: value.url,
                  description: undefined,
                  fileUuid: undefined,
                  hostedIntegrationDocumentId: undefined,
                  hostedIntegrationDocumentPath: undefined,
                  thumbnailUuid: undefined,
                  hostedIntegrationMimeType: undefined
                },
              ],
              documentsToDelete: [],
              integrationType: undefined
            },
          },
        });
      }
    },
  });

  const { user } = useUser(); 

  return (
    <div className="overflow-y-auto w-full flex flex-col h-full">
      <div className="p-5 space-y-5">
        <form.Field
          name="name"
          children={({ state, handleChange }) => (
            <Input
              label="Source name (optional)"
              onBlur={() => void form.handleSubmit()}
              value={state.value}
              inputClassname="h-10!"
              onChange={(e) => handleChange(e.target.value)}
            />
          )}
        />
        <form
          className="flex w-full"
          onSubmit={(e) => {
            e.preventDefault();

            e.stopPropagation();

            void urlForm.handleSubmit();
          }}
        >
          <urlForm.Field
            name="url"
            children={({ handleChange, state }) => (
              <Input
                label="Enter url"
                value={state.value}
                className="grow"
                inputClassname="h-10!"
                inlineButton={{
                  button: {
                    type: 'secondary',
                    text: 'Add',
                    IconEnd: PlusIcon,
                    isSubmit: true,
                    shrink: true,
                    disabled: loading || user.company.limitations.includes(UserCompanyLimitation.cannotCreateQueryMessage),
                  },
                }}
                onChange={(e) => handleChange(e.target.value)}
              />
            )}
          />
        </form>
        {sourceDocuments.indexSourceDocuments.items.map((sd) => (
          <SourceDocumentCard
            FallbackIcon={PictureIcon}
            sourceUuid={source.readSource.uuid}
            sourceType={source.readSource.type}
            sd={sd}
            key={sd.uuid}
            getPath={() => (
              <span className="text-body-smallest text-text-subtle truncate">
                {sd.url}
              </span>
            )}
          />
        ))}
      </div>
    </div>
  );
};
